import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import useClinicChange from '../shared/useClinicChange'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { areValuesEqual } from '@utils/settings.util'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { getClinicNumbers } from '@services/api/phoneManagement.api'
import { IClinicNumber } from '@dataTypes/clinic.type'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { fetchMessagingSettingsApi } from '@services/api/messaging-settings.api'
import { saveMessagingSettings } from '@store/actions'
import {
  IMessagingSettings,
  IMessagingSettingsSave,
} from '@dataTypes/messaging-settings.type'
const LOADING_CONTAINER = 'settings-messaging'

export type IMessagingNumberOption = {
  clinic_number_id: string
  name: string
  phone_number: string
}

export interface IMessagingHooks {
  data: IMessagingSettings
  handleChange: <K extends keyof IMessagingSettings>(
    key: K,
    value: IMessagingSettings[K],
  ) => void
  handleNumberChange: (clinicNumberId: string) => void
  handleReset: () => void
  handleSave: () => Promise<void>
  hasChanges: boolean
  loading: boolean
  dataReady: boolean
  numberOptions: IMessagingNumberOption[]
  savedBaseline: IMessagingSettings
  setHeaderButtons: Dispatch<SetStateAction<ReactNode>>
  showUnsavedModal: boolean
  onConfirmDiscardNavigation: () => void
  onCancelDiscardNavigation: () => void
  validationErrors: { duringHours?: string; afterHours?: string }
}

const DEFAULT_DURING_HOURS_MESSAGE =
  'Thank you for contacting our office! We received your message and will respond shortly during business hours. For emergencies, please call our office.'

const DEFAULT_AFTER_HOURS_MESSAGE =
  "Our office is currently closed. We'll get back to you during business hours. For dental emergencies, please call our office."

const buildDefaultSettings = (
  clinicId: string,
  defaultSmsClinicNumberId = '',
): IMessagingSettings => ({
  clinic_id: clinicId,
  default_sms_clinic_number_id: defaultSmsClinicNumberId || null,
  enable_sms: true,
  enable_mms: true,
  send_appointment_reminders: true,
  allow_confirm_via_sms: true,
  allow_stop_opt_outs: true,
  track_message_status: false,
  auto_reply_during_hours_enabled: true,
  auto_reply_during_hours_message: DEFAULT_DURING_HOURS_MESSAGE,
  auto_reply_after_hours_enabled: true,
  auto_reply_after_hours_message: DEFAULT_AFTER_HOURS_MESSAGE,
})

const dtoToForm = (dto: IMessagingSettings): IMessagingSettings => ({
  clinic_id: dto.clinic_id,
  default_sms_clinic_number_id: dto.default_sms_clinic_number_id ?? null,
  enable_sms: dto.enable_sms,
  enable_mms: dto.enable_mms,
  send_appointment_reminders: dto.send_appointment_reminders,
  allow_confirm_via_sms: dto.allow_confirm_via_sms,
  allow_stop_opt_outs: dto.allow_stop_opt_outs,
  track_message_status: dto.track_message_status,
  auto_reply_during_hours_enabled: dto.auto_reply_during_hours_enabled,
  auto_reply_during_hours_message: dto.auto_reply_during_hours_message,
  auto_reply_after_hours_enabled: dto.auto_reply_after_hours_enabled,
  auto_reply_after_hours_message: dto.auto_reply_after_hours_message,
})

const formatNumberLabel = (number: IClinicNumber): string[] => {
  const lineName = number.is_main
    ? 'Main clinic number'
    : number.line_name?.trim() || 'Clinic number'
  const formattedPhone = formatPhoneNumber(number.phone_number, true)
  return [lineName, formattedPhone]
}

function readUserIdFromStorage(): string {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return ''
    const u = JSON.parse(raw) as { user_id?: string }
    return u.user_id ?? ''
  } catch {
    return ''
  }
}

const useMessaging = (): IMessagingHooks => {
  const dispatch = useAppDispatch()
  const { setHeaderButtons } = useSettingsHeader()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const selectedClinic = useAppSelector((s) => s.clinic.selectedClinic)
  const clinicId = selectedClinic?.clinic_id ?? ''
  const userId = useMemo(() => readUserIdFromStorage(), [])

  const [savedBaseline, setSavedBaseline] = useState<IMessagingSettings>(
    buildDefaultSettings(clinicId),
  )
  const [data, setData] = useState<IMessagingSettings>(savedBaseline)
  const [numberOptions, setNumberOptions] = useState<IMessagingNumberOption[]>(
    [],
  )
  const [loading, setLoading] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const loadGen = useRef(0)

  useClinicChange(clinicId, () => {
    loadGen.current += 1
    setDataReady(false)
    setLoading(true)
  })
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [validationErrors, setValidationErrors] = useState<{
    duringHours?: string
    afterHours?: string
  }>({})

  const hasChanges = useMemo(() => {
    return !areValuesEqual(
      [
        data.default_sms_clinic_number_id ?? '',
        data.enable_sms,
        data.enable_mms,
        data.send_appointment_reminders,
        data.allow_confirm_via_sms,
        data.allow_stop_opt_outs,
        data.track_message_status,
      ],
      [
        savedBaseline.default_sms_clinic_number_id ?? '',
        savedBaseline.enable_sms,
        savedBaseline.enable_mms,
        savedBaseline.send_appointment_reminders,
        savedBaseline.allow_confirm_via_sms,
        savedBaseline.allow_stop_opt_outs,
        savedBaseline.track_message_status,
      ],
    )
  }, [data, savedBaseline])

  useEffect(() => {
    if (!hasChanges) return
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hasChanges])

  useSettingsSubpageLoading(!dataReady)

  const loadNumberOptions = useCallback(async (): Promise<string> => {
    const res = await getClinicNumbers()
    const numbers = (res.data?.clinic_numbers ?? []).filter(
      (n) => n.clinic_id === clinicId && n.status?.toLowerCase() !== 'released',
    )
    const options = numbers.map((number) => {
      const labels = formatNumberLabel(number)
      return {
        clinic_number_id: number.clinic_number_id ?? '',
        name: labels[0],
        phone_number: labels[1],
      }
    })
    setNumberOptions(options)
    return (
      numbers.find((n) => n.is_main)?.clinic_number_id ??
      numbers[0]?.clinic_number_id ??
      ''
    )
  }, [clinicId])

  const loadClinicSettings = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    setLoading(true)
    try {
      const res = await fetchMessagingSettingsApi(clinicId)
      if (res.statusCode !== 200 || !res.data?.messaging_settings) {
        showToast(
          createFailedToast(
            'Messaging',
            res.message || 'Failed to load settings',
          ),
        )
        return
      }
      const mapped = dtoToForm(res.data.messaging_settings)
      setSavedBaseline(mapped)
      setData(mapped)
      setValidationErrors({})
    } catch {
      showToast(
        createFailedToast('Messaging', 'Failed to load messaging settings'),
      )
    } finally {
      setLoading(false)
    }
  }, [clinicId, showToast])

  const loadInitial = useCallback(async (): Promise<void> => {
    const gen = ++loadGen.current
    if (!clinicId) {
      setLoading(false)
      setDataReady(true)
      return
    }
    setLoading(true)
    try {
      const defaultNumberId = await loadNumberOptions()
      await loadClinicSettings()
      setData((prev) => ({
        ...prev,
        default_sms_clinic_number_id:
          prev.default_sms_clinic_number_id ?? defaultNumberId ?? null,
      }))
    } catch {
      showToast(
        createFailedToast('Messaging', 'Failed to load messaging settings'),
      )
    } finally {
      if (loadGen.current === gen) {
        setLoading(false)
        setDataReady(true)
      }
    }
  }, [clinicId, loadNumberOptions, loadClinicSettings, showToast])

  useEffect(() => {
    void loadInitial()
  }, [loadInitial])

  const handleNumberChange = useCallback((clinicNumberId: string): void => {
    setData((prev) => ({
      ...prev,
      default_sms_clinic_number_id: clinicNumberId || null,
    }))
  }, [])

  const handleChange = <K extends keyof IMessagingSettings>(
    key: K,
    value: IMessagingSettings[K],
  ): void => {
    setData((prev) => ({ ...prev, [key]: value }))
    if (
      [
        'auto_reply_during_hours_message',
        'auto_reply_after_hours_message',
        'auto_reply_during_hours_enabled',
        'auto_reply_after_hours_enabled',
      ].includes(key)
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        duringHours: [
          'auto_reply_during_hours_message',
          'auto_reply_during_hours_enabled',
        ].includes(key)
          ? undefined
          : prev.duringHours,
        afterHours: [
          'auto_reply_after_hours_message',
          'auto_reply_after_hours_enabled',
        ].includes(key)
          ? undefined
          : prev.afterHours,
      }))
    }
  }

  const handleReset = useCallback((): void => {
    setData({ ...savedBaseline })
    setValidationErrors({})
  }, [savedBaseline])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!clinicId) return
    setLoading(true)
    showLoading({ container: LOADING_CONTAINER })
    try {
      const messagingWithoutAutoReply: IMessagingSettingsSave = {
        messaging_settings_id: data.messaging_settings_id,
        clinic_id: clinicId,
        default_sms_clinic_number_id: data.default_sms_clinic_number_id,
        enable_sms: data.enable_sms,
        enable_mms: data.enable_mms,
        send_appointment_reminders: data.send_appointment_reminders,
        allow_confirm_via_sms: data.allow_confirm_via_sms,
        allow_stop_opt_outs: data.allow_stop_opt_outs,
        track_message_status: data.track_message_status,
        updated_by: userId || null,
      }
      const result = await dispatch(
        saveMessagingSettings(messagingWithoutAutoReply),
      ).unwrap()
      const mapped = dtoToForm(result)
      setSavedBaseline(mapped)
      setData(mapped)
      setValidationErrors({})
      showToast(createSuccessToast('Saved', 'Messaging settings saved.'))
    } catch (err) {
      showToast(
        createFailedToast(
          'Messaging',
          typeof err === 'string' ? err : 'Failed to save settings',
        ),
      )
    } finally {
      setLoading(false)
      hideLoading(LOADING_CONTAINER)
    }
  }, [clinicId, data, dispatch, hideLoading, showLoading, showToast, userId])

  const onConfirmDiscardNavigation = useCallback((): void => {
    setShowUnsavedModal(false)
    setData({ ...savedBaseline })
    setValidationErrors({})
  }, [savedBaseline])

  const onCancelDiscardNavigation = useCallback((): void => {
    setShowUnsavedModal(false)
  }, [])

  return {
    data,
    handleChange,
    handleNumberChange,
    handleReset,
    handleSave,
    hasChanges,
    loading,
    dataReady,
    numberOptions,
    savedBaseline,
    setHeaderButtons,
    showUnsavedModal,
    onConfirmDiscardNavigation,
    onCancelDiscardNavigation,
    validationErrors,
  }
}

export default useMessaging
